import {
  createContext,
  type Dispatch,
  type SetStateAction,
  useContext,
  useLayoutEffect,
} from "react";

/** What a page needs from the store's chrome, which stays mounted while pages change under it. */
export interface ChromeHints {
  /** The page opens on a stage that runs under a floating nav (Home's hero). */
  underNav?: boolean;
  /** The page's stage is under the top bars right now: they turn to glass over it. */
  overStage?: boolean;
  /** An inner page: the phone's top bar shows Back and the parent's name. */
  back?: { label: string; href: string };
  /** The page draws its own MobileActionBar (a commerce page), so the tab bar steps aside. */
  hideTabs?: boolean;
}

/** StoreChrome's hints state setter; null outside one. */
export const ChromeHintsContext = createContext<Dispatch<SetStateAction<ChromeHints>> | null>(null);

/**
 * Tells the store's chrome what this page needs while it is mounted, and takes it back when it
 * unmounts. A page template's layout calls it, and so may a section that knows a hint the layout
 * cannot (the box page's breadcrumb knows the parent to go back to): each caller sets only the
 * hints it names, and takes back only those. Outside StoreChrome it does nothing.
 */
export function useChromeHints({ underNav, overStage, back, hideTabs }: ChromeHints): void {
  const set = useContext(ChromeHintsContext);
  const backLabel = back?.label;
  const backHref = back?.href;
  // Before paint, so the page never shows a frame with the previous page's padding.
  useLayoutEffect(() => {
    if (!set) return;
    const mine: ChromeHints = {};
    if (underNav !== undefined) mine.underNav = underNav;
    if (overStage !== undefined) mine.overStage = overStage;
    if (hideTabs !== undefined) mine.hideTabs = hideTabs;
    if (backLabel !== undefined && backHref !== undefined) {
      mine.back = { label: backLabel, href: backHref };
    }
    set((hints) => ({ ...hints, ...mine }));
    return () =>
      set((hints) => {
        const next = { ...hints };
        for (const key of Object.keys(mine) as (keyof ChromeHints)[]) delete next[key];
        return next;
      });
  }, [set, underNav, overStage, hideTabs, backLabel, backHref]);
}
